import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";

import { api } from "../../lib/api";
import { formatInr, formatInrLoose } from "../../lib/currency";
import { useAuth } from "../../providers/AuthProvider";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { ReportsDateRangeFilterBar } from "../components/reports/ReportsDateRangeFilterBar";

type SalesSummaryData = {
  total_revenue: string;
  total_orders: number;
  total_items_sold: string;
  avg_order_value: string;
};

type SalesTrendRow = {
  date: string;
  total_sales: string;
};

function parseMoney(v: string) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

export function ReportsSalesPage() {
  const { token } = useAuth();

  const [fromDate, setFromDate] = useState<string | null>(null);
  const [toDate, setToDate] = useState<string | null>(null);

  const [summary, setSummary] = useState<SalesSummaryData | null>(null);
  const [trend, setTrend] = useState<SalesTrendRow[]>([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    const run = async () => {
      try {
        const [sumRes, trendRes] = await Promise.all([
          api.reports.getSalesSummary({
            token,
            filters: { from_date: fromDate, to_date: toDate },
          }),
          api.reports.getSalesTrend({
            token,
            filters: { from_date: fromDate, to_date: toDate },
          }),
        ]);

        if (cancelled) return;

        setSummary(sumRes.data as SalesSummaryData);
        setTrend(trendRes.data as SalesTrendRow[]);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : "Failed to load reports");
        setSummary(null);
        setTrend([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [token, fromDate, toDate]);

  const chartData = useMemo(() => {
    return trend.map((r) => ({
      date: r.date,
      total_sales: parseMoney(r.total_sales),
    }));
  }, [trend]);

  const totalRevenue = summary ? formatInr(parseMoney(summary.total_revenue)) : formatInr(0);
  const totalOrders = summary?.total_orders ?? 0;
  const totalItemsSold = summary?.total_items_sold ?? "0.000";
  const avgOrderValue = summary ? formatInr(parseMoney(summary.avg_order_value)) : formatInr(0);

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:flex-wrap lg:items-start lg:justify-between">
        <div>
          <h1 className="mb-1 text-2xl font-semibold text-foreground md:text-3xl">Sales reports</h1>
          <p className="text-muted-foreground">Revenue, orders, and trends for your business.</p>
        </div>

        <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
          <ReportsDateRangeFilterBar
            fromDate={fromDate}
            toDate={toDate}
            onChange={({ fromDate: nextFrom, toDate: nextTo }) => {
              setFromDate(nextFrom);
              setToDate(nextTo);
            }}
          />
          <div className="sm:self-end">
            <Link
              to="/reports"
              className="text-sm text-muted-foreground underline underline-offset-2 hover:text-foreground"
            >
              Back to hub
            </Link>
          </div>
        </div>
      </div>

      {error ? (
        <Alert variant="destructive">
          <AlertTitle>Something went wrong</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total revenue</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold">{totalRevenue}</div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total orders</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold">{totalOrders}</div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Items sold</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold">{totalItemsSold}</div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Avg. order value</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold">{avgOrderValue}</div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle>Sales trend</CardTitle>
          <CardDescription>Grouped by day (based on sale date).</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-muted-foreground">Loading chart...</div>
          ) : chartData.length === 0 ? (
            <div className="text-muted-foreground">No sales data for the selected range.</div>
          ) : (
            <div className="h-[260px] min-h-[220px] w-full min-w-0 md:h-[320px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tickLine={false} axisLine={false} minTickGap={10} />
                  <YAxis tickFormatter={(v) => formatInrLoose(Number(v))} tickLine={false} axisLine={false} />
                  <Tooltip
                    formatter={(value: number) => [formatInrLoose(value), "Total sales"]}
                    labelFormatter={(label) => label}
                  />
                  <Line type="monotone" dataKey="total_sales" stroke="var(--chart-1)" dot={false} strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

